import { useState, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  CheckCircle2,
  XCircle,
  Loader2,
  ChevronDown,
  ChevronRight,
  Edit3,
  Check,
  X,
  AlertTriangle,
  FileText,
  FileSpreadsheet,
  File,
  Trash2,
  ShieldCheck,
  Eye,
  Flag,
  ArrowUpDown,
} from 'lucide-react'
import api from '../services/api'
import { useLanguage } from '../hooks/useLanguage'
import Modal, { ModalActions, ModalButton } from './Modal'

// ─── Types ──────────────────────────────────────────────────────────────────

interface StagingImport {
  id: string
  file_name: string
  file_type: string
  document_type: string
  total_transactions: number
  valid_transactions: number
  avg_confidence: number
  min_confidence: number
  status: 'pending' | 'confirmed' | 'rejected' | 'imported'
  created_at: string
  validation_issues?: Array<{ type: string; message: string; severity: string }>
}

interface StagingTransaction {
  id: string
  staging_id: string
  date: string
  description: string
  amount: number
  currency: string
  counterparty: string | null
  category: string | null
  transaction_type: string | null
  confidence_date: number
  confidence_amount: number
  confidence_description: number
  confidence_counterparty: number
  min_confidence: number
  needs_review: boolean
  user_edited: boolean
  status: 'pending' | 'approved' | 'rejected'
}

interface StagingDetail extends StagingImport {
  transactions: StagingTransaction[]
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function confidenceColor(c: number): string {
  if (c >= 0.9) return 'text-emerald-600 dark:text-emerald-400'
  if (c >= 0.7) return 'text-amber-600 dark:text-amber-400'
  return 'text-red-600 dark:text-red-400'
}

function confidenceBg(c: number): string {
  if (c >= 0.9) return 'bg-emerald-50 dark:bg-emerald-900/20'
  if (c >= 0.7) return 'bg-amber-50 dark:bg-amber-900/20'
  return 'bg-red-50 dark:bg-red-900/20'
}

function confidenceDot(c: number): string {
  if (c >= 0.9) return 'bg-emerald-500'
  if (c >= 0.7) return 'bg-amber-500'
  return 'bg-red-500'
}

function formatCurrency(amount: number, currency = 'EUR'): string {
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(amount)
}

function formatDate(dateStr: string): string {
  try {
    return new Date(dateStr).toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })
  } catch {
    return dateStr
  }
}

function getFileIcon(type: string) {
  switch (type) {
    case 'pdf':
      return <FileText className="w-4 h-4 text-red-500" />
    case 'xlsx':
    case 'xls':
    case 'excel':
      return <FileSpreadsheet className="w-4 h-4 text-green-600" />
    case 'csv':
      return <File className="w-4 h-4 text-blue-500" />
    default:
      return <File className="w-4 h-4 text-gray-400" />
  }
}

// ─── Inline Edit Cell ───────────────────────────────────────────────────────

function EditableCell({
  value,
  confidence,
  onSave,
  type = 'text',
}: {
  value: string
  confidence: number
  onSave: (val: string) => void
  type?: 'text' | 'number' | 'date'
}) {
  const [editing, setEditing] = useState(false)
  const [editValue, setEditValue] = useState(value)

  const handleSave = () => {
    onSave(editValue)
    setEditing(false)
  }

  const handleCancel = () => {
    setEditValue(value)
    setEditing(false)
  }

  if (editing) {
    return (
      <div className="flex items-center gap-1">
        <input
          type={type}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleSave()
            if (e.key === 'Escape') handleCancel()
          }}
          autoFocus
          className="w-full px-1.5 py-0.5 text-xs border border-primary-400 rounded bg-white dark:bg-slate-700 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-primary-500"
        />
        <button onClick={handleSave} className="p-0.5 text-emerald-600 hover:text-emerald-700">
          <Check className="w-3 h-3" />
        </button>
        <button onClick={handleCancel} className="p-0.5 text-gray-400 hover:text-red-500">
          <X className="w-3 h-3" />
        </button>
      </div>
    )
  }

  return (
    <div className={`group/cell flex items-center gap-1 px-1 py-0.5 rounded ${confidenceBg(confidence)}`}>
      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${confidenceDot(confidence)}`} />
      <span className="text-xs text-gray-900 dark:text-gray-100 truncate">{value || '—'}</span>
      <button
        onClick={() => setEditing(true)}
        className="opacity-0 group-hover/cell:opacity-100 p-0.5 text-gray-400 hover:text-primary-600 transition-opacity"
        title="Modifica"
      >
        <Edit3 className="w-3 h-3" />
      </button>
    </div>
  )
}

// ─── Transaction Table ──────────────────────────────────────────────────────

function TransactionTable({
  transactions,
  stagingId,
  onUpdate,
}: {
  transactions: StagingTransaction[]
  stagingId: string
  onUpdate: () => void
}) {
  const { language } = useLanguage()
  const queryClient = useQueryClient()
  const [sortField, setSortField] = useState<'date' | 'amount' | 'confidence'>('date')
  const [sortAsc, setSortAsc] = useState(true)

  const updateMutation = useMutation({
    mutationFn: async ({ txId, field, value }: { txId: string; field: string; value: string }) => {
      await api.patch(`/import-staging/${stagingId}/transactions/${txId}`, { [field]: value })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['staging-detail', stagingId] })
      onUpdate()
    },
  })

  const toggleSort = (field: 'date' | 'amount' | 'confidence') => {
    if (sortField === field) {
      setSortAsc(!sortAsc)
    } else {
      setSortField(field)
      setSortAsc(true)
    }
  }

  const sorted = [...transactions].sort((a, b) => {
    let cmp = 0
    if (sortField === 'date') cmp = a.date.localeCompare(b.date)
    else if (sortField === 'amount') cmp = a.amount - b.amount
    else cmp = a.min_confidence - b.min_confidence
    return sortAsc ? cmp : -cmp
  })

  const SortHeader = ({ field, label }: { field: 'date' | 'amount' | 'confidence'; label: string }) => (
    <th
      className="px-2 py-2 text-left text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:text-gray-700 dark:hover:text-gray-200 select-none"
      onClick={() => toggleSort(field)}
    >
      <span className="flex items-center gap-1">
        {label}
        <ArrowUpDown className={`w-3 h-3 ${sortField === field ? 'text-primary-600' : 'text-gray-300'}`} />
      </span>
    </th>
  )

  const handleCellSave = (txId: string, field: string, value: string) => {
    updateMutation.mutate({ txId, field, value })
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left">
        <thead className="border-b border-gray-200 dark:border-slate-600">
          <tr>
            <th className="w-8 px-2 py-2" />
            <SortHeader field="date" label={language === 'it' ? 'Data' : 'Date'} />
            <th className="px-2 py-2 text-left text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              {language === 'it' ? 'Descrizione' : 'Description'}
            </th>
            <SortHeader field="amount" label={language === 'it' ? 'Importo' : 'Amount'} />
            <th className="px-2 py-2 text-left text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              {language === 'it' ? 'Controparte' : 'Counterparty'}
            </th>
            <th className="px-2 py-2 text-left text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              {language === 'it' ? 'Tipo' : 'Type'}
            </th>
            <SortHeader field="confidence" label="Conf." />
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
          {sorted.map((tx) => (
            <tr
              key={tx.id}
              className={`hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors ${
                tx.needs_review ? 'bg-amber-50/50 dark:bg-amber-900/10' : ''
              } ${tx.status === 'rejected' ? 'opacity-40 line-through' : ''}`}
            >
              {/* Review flag */}
              <td className="px-2 py-1.5">
                {tx.needs_review && (
                  <span title="Da verificare"><Flag className="w-3.5 h-3.5 text-amber-500" /></span>
                )}
                {tx.user_edited && (
                  <span title="Modificato manualmente"><Edit3 className="w-3.5 h-3.5 text-primary-500" /></span>
                )}
              </td>

              {/* Date */}
              <td className="px-2 py-1.5 whitespace-nowrap">
                <EditableCell
                  value={formatDate(tx.date)}
                  confidence={tx.confidence_date}
                  onSave={(v) => handleCellSave(tx.id, 'date', v)}
                  type="text"
                />
              </td>

              {/* Description */}
              <td className="px-2 py-1.5 max-w-[220px]">
                <EditableCell
                  value={tx.description}
                  confidence={tx.confidence_description}
                  onSave={(v) => handleCellSave(tx.id, 'description', v)}
                />
              </td>

              {/* Amount */}
              <td className="px-2 py-1.5 whitespace-nowrap">
                <EditableCell
                  value={formatCurrency(tx.amount, tx.currency)}
                  confidence={tx.confidence_amount}
                  onSave={(v) => handleCellSave(tx.id, 'amount', v)}
                  type="text"
                />
              </td>

              {/* Counterparty */}
              <td className="px-2 py-1.5 max-w-[160px]">
                <EditableCell
                  value={tx.counterparty || ''}
                  confidence={tx.confidence_counterparty}
                  onSave={(v) => handleCellSave(tx.id, 'counterparty', v)}
                />
              </td>

              {/* Type */}
              <td className="px-2 py-1.5 whitespace-nowrap">
                <span className={`text-xs px-1.5 py-0.5 rounded ${
                  tx.transaction_type === 'credit' 
                    ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' 
                    : tx.transaction_type === 'debit'
                    ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                    : 'bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-gray-400'
                }`}>
                  {tx.transaction_type === 'credit' ? '↗ Entrata' : tx.transaction_type === 'debit' ? '↙ Uscita' : '—'}
                </span>
              </td>

              {/* Confidence */}
              <td className="px-2 py-1.5 whitespace-nowrap">
                <span className={`text-xs font-mono font-medium ${confidenceColor(tx.min_confidence)}`}>
                  {(tx.min_confidence * 100).toFixed(0)}%
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {transactions.length === 0 && (
        <div className="text-center py-8 text-sm text-gray-400">
          {language === 'it' ? 'Nessuna transazione estratta' : 'No transactions extracted'}
        </div>
      )}
    </div>
  )
}

// ─── Confidence Bar ─────────────────────────────────────────────────────────

function ConfidenceBar({ value, size = 'sm' }: { value: number; size?: 'sm' | 'md' }) {
  const pct = Math.round(value * 100)
  const w = size === 'md' ? 'w-24' : 'w-16'
  const h = size === 'md' ? 'h-2' : 'h-1.5'
  return (
    <div className="flex items-center gap-2">
      <div className={`${w} ${h} rounded-full bg-gray-200 dark:bg-slate-600 overflow-hidden`}>
        <div
          className={`${h} rounded-full transition-all ${
            pct >= 90 ? 'bg-emerald-500' : pct >= 70 ? 'bg-amber-500' : 'bg-red-500'
          }`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className={`text-xs font-mono font-medium ${confidenceColor(value)}`}>{pct}%</span>
    </div>
  )
}

// ─── Staging Import Card ────────────────────────────────────────────────────

function StagingCard({
  imp,
  onConfirm,
  onReject,
}: {
  imp: StagingImport
  onConfirm: (id: string) => void
  onReject: (id: string) => void
}) {
  const { language } = useLanguage()
  const [expanded, setExpanded] = useState(false)
  const queryClient = useQueryClient()

  // Fetch detail only when expanded
  const { data: detail, isLoading: detailLoading } = useQuery<StagingDetail>({
    queryKey: ['staging-detail', imp.id],
    queryFn: async () => {
      const res = await api.get(`/import-staging/${imp.id}`)
      return res.data
    },
    enabled: expanded,
  })

  const reviewCount = detail?.transactions?.filter((t) => t.needs_review).length ?? 0
  const issueCount = imp.validation_issues?.length ?? 0

  // Document type label mapping
  const docTypeLabel = (type: string): string => {
    const labels: Record<string, { it: string; en: string }> = {
      bank_statement: { it: 'Estratto Conto', en: 'Bank Statement' },
      accounting_statement: { it: 'Situazione Contabile', en: 'Accounting Statement' },
      invoice: { it: 'Fattura', en: 'Invoice' },
      receipt: { it: 'Ricevuta', en: 'Receipt' },
      tax_document: { it: 'Documento Fiscale', en: 'Tax Document' },
      contract: { it: 'Contratto', en: 'Contract' },
      payslip: { it: 'Busta Paga', en: 'Payslip' },
      unknown: { it: 'Non classificato', en: 'Unclassified' },
    }
    const l = labels[type]
    return l ? l[language as 'it' | 'en'] || type : type.replace(/_/g, ' ')
  }

  return (
    <div className="border border-gray-200 dark:border-slate-600 rounded-xl overflow-hidden bg-white dark:bg-slate-800 shadow-sm">
      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors text-left"
      >
        {expanded ? (
          <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
        ) : (
          <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
        )}

        {getFileIcon(imp.file_type)}

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
              {imp.file_name}
            </span>
            {imp.document_type && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 uppercase font-medium">
                {docTypeLabel(imp.document_type)}
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 mt-0.5">
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {imp.total_transactions} {language === 'it' ? 'transazioni' : 'transactions'}
              {imp.valid_transactions < imp.total_transactions && (
                <span className="text-amber-600"> ({imp.valid_transactions} {language === 'it' ? 'valide' : 'valid'})</span>
              )}
            </span>
            <span className="text-xs text-gray-400">•</span>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {formatDate(imp.created_at)}
            </span>
          </div>
        </div>

        {/* Confidence */}
        <div className="flex-shrink-0 hidden sm:block">
          <ConfidenceBar value={imp.avg_confidence} size="md" />
        </div>

        {/* Issues badge */}
        {(reviewCount > 0 || issueCount > 0) && (
          <span className="flex-shrink-0 flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400">
            <AlertTriangle className="w-3 h-3" />
            {reviewCount + issueCount}
          </span>
        )}

        {/* Status */}
        {imp.status === 'imported' && (
          <span className="flex-shrink-0 flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400">
            <CheckCircle2 className="w-3 h-3" />
            {language === 'it' ? 'Importato' : 'Imported'}
          </span>
        )}
        {imp.status === 'rejected' && (
          <span className="flex-shrink-0 flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400">
            <XCircle className="w-3 h-3" />
            {language === 'it' ? 'Scartato' : 'Rejected'}
          </span>
        )}
      </button>

      {/* Expanded detail */}
      {expanded && (
        <div className="border-t border-gray-200 dark:border-slate-600">
          {detailLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
            </div>
          ) : detail ? (
            <>
              {/* Validation issues */}
              {detail.validation_issues && detail.validation_issues.length > 0 && (
                <div className="px-4 py-2 bg-amber-50 dark:bg-amber-900/10 border-b border-amber-200 dark:border-amber-800/30">
                  <div className="flex items-center gap-2 mb-1">
                    <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />
                    <span className="text-xs font-semibold text-amber-800 dark:text-amber-300">
                      {language === 'it' ? 'Avvisi di validazione' : 'Validation warnings'}
                    </span>
                  </div>
                  <ul className="space-y-0.5">
                    {detail.validation_issues.slice(0, 5).map((issue, i) => (
                      <li key={i} className="text-xs text-amber-700 dark:text-amber-400 flex items-center gap-1.5">
                        <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                          issue.severity === 'error' ? 'bg-red-500' : 'bg-amber-500'
                        }`} />
                        {issue.message}
                      </li>
                    ))}
                    {detail.validation_issues.length > 5 && (
                      <li className="text-xs text-amber-500">
                        +{detail.validation_issues.length - 5} {language === 'it' ? 'altri' : 'more'}...
                      </li>
                    )}
                  </ul>
                </div>
              )}

              {/* Confidence legend */}
              <div className="px-4 py-2 bg-gray-50 dark:bg-slate-800/50 border-b border-gray-200 dark:border-slate-600 flex items-center gap-4">
                <span className="text-[10px] text-gray-500 dark:text-gray-400 uppercase font-medium">
                  {language === 'it' ? 'Confidenza:' : 'Confidence:'}
                </span>
                <span className="flex items-center gap-1 text-[10px] text-gray-600 dark:text-gray-300">
                  <span className="w-2 h-2 rounded-full bg-emerald-500" /> ≥90%
                </span>
                <span className="flex items-center gap-1 text-[10px] text-gray-600 dark:text-gray-300">
                  <span className="w-2 h-2 rounded-full bg-amber-500" /> 70-90%
                </span>
                <span className="flex items-center gap-1 text-[10px] text-gray-600 dark:text-gray-300">
                  <span className="w-2 h-2 rounded-full bg-red-500" /> &lt;70%
                </span>
                <span className="flex items-center gap-1 text-[10px] text-gray-600 dark:text-gray-300 ml-2">
                  <Flag className="w-3 h-3 text-amber-500" /> {language === 'it' ? 'Da verificare' : 'Needs review'}
                </span>
              </div>

              {/* Transaction table */}
              <TransactionTable
                transactions={detail.transactions}
                stagingId={imp.id}
                onUpdate={() => queryClient.invalidateQueries({ queryKey: ['staging-list'] })}
              />

              {/* Action buttons */}
              {imp.status === 'pending' && (
                <div className="flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-slate-800/50 border-t border-gray-200 dark:border-slate-600">
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {detail.transactions.filter((t) => t.needs_review).length > 0 ? (
                      <span className="flex items-center gap-1 text-amber-600">
                        <AlertTriangle className="w-3 h-3" />
                        {detail.transactions.filter((t) => t.needs_review).length}{' '}
                        {language === 'it' ? 'righe da verificare' : 'rows need review'}
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-emerald-600">
                        <ShieldCheck className="w-3 h-3" />
                        {language === 'it' ? 'Tutte le righe sono validate' : 'All rows validated'}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => onReject(imp.id)}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-700 dark:text-red-400 border border-red-300 dark:border-red-700 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                      {language === 'it' ? 'Scarta' : 'Reject'}
                    </button>
                    <button
                      onClick={() => onConfirm(imp.id)}
                      className="flex items-center gap-1.5 px-4 py-1.5 text-xs font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 transition-colors shadow-sm"
                    >
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      {language === 'it' ? 'Conferma Importazione' : 'Confirm Import'}
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : null}
        </div>
      )}
    </div>
  )
}

// ─── Main Component ─────────────────────────────────────────────────────────

export default function ImportStaging() {
  const { language } = useLanguage()
  const queryClient = useQueryClient()
  const [confirmModal, setConfirmModal] = useState<{ id: string; action: 'confirm' | 'reject' } | null>(null)

  // Fetch staging imports list
  const { data: stagingImports, isLoading } = useQuery<StagingImport[]>({
    queryKey: ['staging-list'],
    queryFn: async () => {
      const res = await api.get('/import-staging')
      return res.data
    },
    refetchInterval: 10000,
  })

  // Confirm import
  const confirmMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await api.post(`/import-staging/${id}/confirm`)
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['staging-list'] })
      queryClient.invalidateQueries({ queryKey: ['uploads'] })
      queryClient.invalidateQueries({ queryKey: ['transactions'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
      queryClient.invalidateQueries({ queryKey: ['reports-dashboard'] })
      queryClient.invalidateQueries({ queryKey: ['uncategorized-transactions'] })
      setConfirmModal(null)
    },
  })

  // Reject import
  const rejectMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await api.post(`/import-staging/${id}/reject`)
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['staging-list'] })
      setConfirmModal(null)
    },
  })

  const handleAction = useCallback(() => {
    if (!confirmModal) return
    if (confirmModal.action === 'confirm') {
      confirmMutation.mutate(confirmModal.id)
    } else {
      rejectMutation.mutate(confirmModal.id)
    }
  }, [confirmModal, confirmMutation, rejectMutation])

  // Only show pending imports
  const pendingImports = stagingImports?.filter((i) => i.status === 'pending') ?? []
  const recentImports = stagingImports?.filter((i) => i.status !== 'pending').slice(0, 5) ?? []

  if (isLoading) return null
  if (!stagingImports || stagingImports.length === 0) return null

  return (
    <>
      {/* Pending Imports */}
      {pendingImports.length > 0 && (
        <div className="space-y-3" data-tour="staging-preview">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <Eye className="w-4 h-4 text-primary-600" />
              {language === 'it' ? 'Anteprima Importazioni' : 'Import Preview'}
              <span className="text-xs px-1.5 py-0.5 rounded-full bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 font-medium">
                {pendingImports.length}
              </span>
            </h3>
            <p className="text-xs text-gray-400 dark:text-gray-500">
              {language === 'it'
                ? 'Verifica e conferma prima di importare'
                : 'Review and confirm before importing'}
            </p>
          </div>

          <div className="space-y-2">
            {pendingImports.map((imp) => (
              <StagingCard
                key={imp.id}
                imp={imp}
                onConfirm={(id) => setConfirmModal({ id, action: 'confirm' })}
                onReject={(id) => setConfirmModal({ id, action: 'reject' })}
              />
            ))}
          </div>
        </div>
      )}

      {/* Recent imported/rejected */}
      {recentImports.length > 0 && pendingImports.length === 0 && (
        <div className="space-y-2">
          <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
            {language === 'it' ? 'Importazioni recenti' : 'Recent imports'}
          </h4>
          {recentImports.map((imp) => (
            <StagingCard
              key={imp.id}
              imp={imp}
              onConfirm={() => {}}
              onReject={() => {}}
            />
          ))}
        </div>
      )}

      {/* Confirmation Modal */}
      <Modal
        isOpen={!!confirmModal}
        onClose={() => setConfirmModal(null)}
        title={
          confirmModal?.action === 'confirm'
            ? language === 'it'
              ? 'Conferma importazione'
              : 'Confirm import'
            : language === 'it'
            ? 'Scarta importazione'
            : 'Reject import'
        }
        type={confirmModal?.action === 'confirm' ? 'info' : 'warning'}
      >
        <p className="text-sm text-gray-600 dark:text-gray-300">
          {confirmModal?.action === 'confirm'
            ? language === 'it'
              ? 'Le transazioni verificate saranno importate nel sistema. Questa azione non è reversibile.'
              : 'Verified transactions will be imported into the system. This action is irreversible.'
            : language === 'it'
            ? 'L\'importazione sarà definitivamente scartata e il file eliminato. Procedere?'
            : 'The import will be permanently discarded and the file deleted. Proceed?'}
        </p>
        <ModalActions>
          <ModalButton onClick={() => setConfirmModal(null)} variant="secondary">
            {language === 'it' ? 'Annulla' : 'Cancel'}
          </ModalButton>
          <ModalButton
            onClick={handleAction}
            variant={confirmModal?.action === 'confirm' ? 'primary' : 'danger'}
          >
            {(confirmMutation.isPending || rejectMutation.isPending) ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : confirmModal?.action === 'confirm' ? (
              language === 'it' ? 'Conferma' : 'Confirm'
            ) : (
              language === 'it' ? 'Scarta' : 'Reject'
            )}
          </ModalButton>
        </ModalActions>
      </Modal>
    </>
  )
}
